import React from "react";
import { CfiChild } from "./cfiTypes";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FieldArray } from "react-final-form-arrays";
import { MuiAdd, MuiPerson } from "../common/icons";
import MuiDelete from "@mui/icons-material/Delete";
import {
  DateFormField,
  RadioFormField,
  TextFormField,
} from "../common/components/FormFields";
import dayjs from "dayjs";

interface ChildFieldsProps {
  namespace: string;
}

function ChildFields({ namespace }: ChildFieldsProps) {
  const theme = useTheme();

  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Grid container spacing={0.5} flex={1}>
      <Grid item lg={3} sm={6} xs={12}>
        <TextFormField
          name={`${namespace}.name`}
          label="Name"
          required
          icon={<MuiPerson />}
        />
      </Grid>
      <Grid item lg={3} sm={6} xs={12}>
        <DateFormField
          name={`${namespace}.dateOfBirth`}
          label="Date Of Birth"
          required
          stringOutput
          noIcon={sm}
          minDate={dayjs().subtract(20, "years")}
        />
      </Grid>
      <Grid item lg={3} sm={6} xs={12}>
        <TextFormField
          name={`${namespace}.nihb`}
          label="N#"
          required
          noIcon={lg}
        />
      </Grid>
      <Grid item lg={3} sm={6} xs={12}>
        <TextFormField
          name={`${namespace}.healthCardNumber`}
          label="Health Card Number"
          noIcon={sm}
        />
      </Grid>
    </Grid>
  );
}

export const DEFAULT_CHILD_FIELDS: Partial<CfiChild> = {
  name: "",
  dateOfBirth: "",
  nihb: "",
  healthCardNumber: "",
};

export interface ChildrenFieldsProps {}

export function ChildrenFields({}: ChildrenFieldsProps) {
  return (
    <Box>
      <Box pb={1}>
        <Typography variant="h5">Children</Typography>
      </Box>
      <FieldArray<Partial<CfiChild>>
        name="children"
        render={({ fields }) => (
          <Stack gap={1}>
            {fields.map((name, index) => (
              <Stack key={name} direction="row" alignItems="center" gap={1}>
                <ChildFields namespace={name} />
                {(fields.length ?? 0) > 1 && (
                  <IconButton
                    color="error"
                    aria-label={`Remove child ${index + 1}`}
                    onClick={() => fields.remove(index)}
                  >
                    <MuiDelete />
                  </IconButton>
                )}
              </Stack>
            ))}
            <Box ml={5}>
              <Button
                startIcon={<MuiAdd />}
                variant="outlined"
                onClick={() => fields.push({ ...DEFAULT_CHILD_FIELDS })}
              >
                Add Child
              </Button>
            </Box>
            <Box mt={1}>
              <RadioFormField
                name="hasPendingApplications"
                label="Do you currently have any pending applications with Inuit Child First Initiative?"
                options={["Yes", "No"]}
                required
              />
            </Box>
          </Stack>
        )}
      />
    </Box>
  );
}
